import ExcelJS from 'exceljs';
function groupByAgreement(data) {
    const groups = new Map();
    data.forEach((row) => {
        const agreementNo = row.agreement_no || 'Unknown';
        if (!groups.has(agreementNo)) {
            groups.set(agreementNo, {
                agreement_no: agreementNo,
                customer_name: row.customer_name,
                project_name: row.project_name,
                unit_name: row.unit_name,
                paymentTransactions: [],
                additionalWork: [],
            });
        }
        const group = groups.get(agreementNo);
        const isAdditionalWork = row.additional_charge_work_desc || row.additional_charge_amount;
        if (isAdditionalWork) {
            group.additionalWork.push(row);
        }
        else {
            group.paymentTransactions.push(row);
        }
    });
    return Array.from(groups.values());
}
function addHeaderRow(worksheet, rowData, rowNum, bold = true) {
    const row = worksheet.getRow(rowNum);
    rowData.forEach((cell, i) => {
        const cellObj = row.getCell(i + 1);
        cellObj.value = cell;
        if (bold) {
            cellObj.font = { bold: true };
        }
        cellObj.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };
    });
    return row;
}
function addDataRow(worksheet, rowData, rowNum, currencyCols = []) {
    const row = worksheet.getRow(rowNum);
    rowData.forEach((cell, i) => {
        const cellObj = row.getCell(i + 1);
        if (typeof cell === 'object' && 'formula' in cell) {
            cellObj.value = cell;
        }
        else {
            cellObj.value = cell;
            if (currencyCols.includes(i + 1)) {
                cellObj.numFmt = '₹#,##0.00';
            }
        }
        cellObj.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };
        cellObj.alignment = { wrapText: true, vertical: 'middle' };
    });
    return row;
}
function writeCustomerDetails(worksheet, group, startRow) {
    let currentRow = startRow;
    addHeaderRow(worksheet, ['CUSTOMER / AGREEMENT DETAILS'], currentRow);
    currentRow++;
    const detailFields = [
        { label: 'Customer', value: group.customer_name },
        { label: 'Project', value: group.project_name },
        { label: 'Agreement No', value: group.agreement_no },
    ];
    if (group.unit_name) {
        detailFields.push({ label: 'Unit', value: group.unit_name });
    }
    detailFields.forEach((field) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = field.label;
        row.getCell(1).font = { bold: true };
        row.getCell(2).value = field.value;
        row.getCell(1).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        row.getCell(2).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        currentRow++;
    });
    currentRow++;
    return currentRow;
}
function writePaymentTransactions(worksheet, transactions, startRow) {
    let currentRow = startRow;
    addHeaderRow(worksheet, ['PAYMENT TRANSACTIONS'], currentRow);
    currentRow++;
    const paymentHeaders = ['S.No', 'Payment Date', 'Payment Stage', 'Amount', 'Payment Mode', 'Source of Fund', 'Remarks'];
    addHeaderRow(worksheet, paymentHeaders, currentRow);
    currentRow++;
    const paymentStartRow = currentRow;
    transactions.forEach((txn, idx) => {
        const amount = Number(txn.amount || 0);
        const paymentStage = txn.payment_stage_desc || '';
        const remarks = txn.transaction_detail || txn.notes || '';
        addDataRow(worksheet, [idx + 1, txn.date, paymentStage, amount, txn.payment_mode, txn.source_of_fund_name, remarks], currentRow, [4]);
        currentRow++;
    });
    const paymentEndRow = currentRow - 1;
    let totalRow = null;
    if (transactions.length > 0) {
        totalRow = currentRow;
        const totalRowObj = worksheet.getRow(currentRow);
        totalRowObj.getCell(1).value = '';
        totalRowObj.getCell(2).value = '';
        totalRowObj.getCell(3).value = 'Total Payment Transactions';
        totalRowObj.getCell(3).font = { bold: true };
        totalRowObj.getCell(4).value = { formula: `SUM(D${paymentStartRow}:D${paymentEndRow})`, result: 0 };
        totalRowObj.getCell(4).numFmt = '₹#,##0.00';
        totalRowObj.getCell(4).font = { bold: true };
        for (let i = 1; i <= 7; i++) {
            totalRowObj.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        }
        currentRow++;
    }
    currentRow++;
    const totalAmount = transactions.reduce((sum, txn) => sum + Number(txn.amount || 0), 0);
    return { endRow: currentRow - 1, totalRow, totalAmount };
}
function writeAdditionalWork(worksheet, transactions, startRow) {
    let currentRow = startRow;
    addHeaderRow(worksheet, ['ADDITIONAL WORK'], currentRow);
    currentRow++;
    const additionalHeaders = ['S.No', 'Date', 'Additional Work Description', 'Stage Description', 'Amount', 'Payment Mode', 'Source of Fund', 'Remarks'];
    addHeaderRow(worksheet, additionalHeaders, currentRow);
    currentRow++;
    const additionalStartRow = currentRow;
    transactions.forEach((txn, idx) => {
        const amount = Number(txn.additional_charge_amount || txn.amount || 0);
        const workDesc = txn.additional_charge_work_desc || '';
        const stageDesc = txn.additional_charge_stage_desc || '';
        const remarks = txn.transaction_detail || txn.notes || '';
        addDataRow(worksheet, [idx + 1, txn.date, workDesc, stageDesc, amount, txn.payment_mode, txn.source_of_fund_name, remarks], currentRow, [5]);
        currentRow++;
    });
    const additionalEndRow = currentRow - 1;
    let totalRow = null;
    if (transactions.length > 0) {
        totalRow = currentRow;
        const totalRowObj = worksheet.getRow(currentRow);
        totalRowObj.getCell(1).value = '';
        totalRowObj.getCell(2).value = '';
        totalRowObj.getCell(3).value = '';
        totalRowObj.getCell(4).value = 'Total Additional Work';
        totalRowObj.getCell(4).font = { bold: true };
        totalRowObj.getCell(5).value = { formula: `SUM(E${additionalStartRow}:E${additionalEndRow})`, result: 0 };
        totalRowObj.getCell(5).numFmt = '₹#,##0.00';
        totalRowObj.getCell(5).font = { bold: true };
        for (let i = 1; i <= 8; i++) {
            totalRowObj.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        }
        currentRow++;
    }
    currentRow++;
    const totalAmount = transactions.reduce((sum, txn) => sum + Number(txn.additional_charge_amount || txn.amount || 0), 0);
    return { endRow: currentRow - 1, totalRow, totalAmount };
}
function writeGrandTotal(worksheet, paymentTotalRef, additionalTotalRef, startRow, hasPayments, hasAdditionalWork) {
    let currentRow = startRow;
    const grandTotalRow = worksheet.getRow(currentRow);
    grandTotalRow.getCell(1).value = '';
    grandTotalRow.getCell(2).value = '';
    grandTotalRow.getCell(3).value = '';
    grandTotalRow.getCell(4).value = '';
    grandTotalRow.getCell(5).value = 'GRAND TOTAL';
    grandTotalRow.getCell(5).font = { bold: true, size: 12 };
    if (hasPayments && hasAdditionalWork) {
        grandTotalRow.getCell(5).value = { formula: `${paymentTotalRef}+${additionalTotalRef}`, result: 0 };
    }
    else if (hasPayments) {
        grandTotalRow.getCell(4).value = { formula: `${paymentTotalRef}`, result: 0 };
    }
    else if (hasAdditionalWork) {
        grandTotalRow.getCell(5).value = { formula: `${additionalTotalRef}`, result: 0 };
    }
    if (hasPayments) {
        grandTotalRow.getCell(4).numFmt = '₹#,##0.00';
        grandTotalRow.getCell(4).font = { bold: true, size: 12 };
    }
    else if (hasAdditionalWork) {
        grandTotalRow.getCell(5).numFmt = '₹#,##0.00';
        grandTotalRow.getCell(5).font = { bold: true, size: 12 };
    }
    for (let i = 1; i <= 8; i++) {
        grandTotalRow.getCell(i).border = { top: { style: 'double' }, left: { style: 'thin' }, bottom: { style: 'double' }, right: { style: 'thin' } };
    }
    currentRow += 2;
    return currentRow;
}
function writePaymentStageSummary(worksheet, transactions, startRow) {
    let currentRow = startRow;
    addHeaderRow(worksheet, ['STAGE-WISE SUMMARY: PAYMENT STAGES'], currentRow);
    currentRow++;
    const summaryHeaders = ['Payment Stage', 'Schedule Amount', 'Paid Amount', 'Balance'];
    addHeaderRow(worksheet, summaryHeaders, currentRow);
    currentRow++;
    const stageMap = new Map();
    transactions.forEach((txn) => {
        const stage = txn.payment_stage_desc || 'Unknown Stage';
        const existing = stageMap.get(stage) || { scheduleAmount: 0, paidAmount: 0 };
        existing.scheduleAmount = Number(txn.payment_schedule_amount || 0);
        existing.paidAmount += Number(txn.amount || 0);
        stageMap.set(stage, existing);
    });
    const summaryStartRow = currentRow;
    stageMap.forEach((values, stage) => {
        const balance = values.scheduleAmount - values.paidAmount;
        addDataRow(worksheet, [stage, values.scheduleAmount, values.paidAmount, { formula: `B${currentRow}-C${currentRow}`, result: balance }], currentRow, [2, 3, 4]);
        currentRow++;
    });
    const summaryEndRow = currentRow - 1;
    if (stageMap.size > 0) {
        const totalRow = worksheet.getRow(currentRow);
        totalRow.getCell(1).value = 'Total';
        totalRow.getCell(1).font = { bold: true };
        totalRow.getCell(2).value = { formula: `SUM(B${summaryStartRow}:B${summaryEndRow})`, result: 0 };
        totalRow.getCell(3).value = { formula: `SUM(C${summaryStartRow}:C${summaryEndRow})`, result: 0 };
        totalRow.getCell(4).value = { formula: `SUM(D${summaryStartRow}:D${summaryEndRow})`, result: 0 };
        for (let i = 1; i <= 4; i++) {
            totalRow.getCell(i).numFmt = '₹#,##0.00';
            totalRow.getCell(i).font = { bold: true };
            totalRow.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        }
        currentRow++;
    }
    return currentRow;
}
function writeAdditionalWorkStageSummary(worksheet, transactions, startRow) {
    let currentRow = startRow;
    addHeaderRow(worksheet, ['STAGE-WISE SUMMARY: ADDITIONAL WORK'], currentRow);
    currentRow++;
    const additionalSummaryHeaders = ['Additional Work Description', 'Stage Description', 'Charge Amount', 'Paid Amount', 'Balance'];
    addHeaderRow(worksheet, additionalSummaryHeaders, currentRow);
    currentRow++;
    const additionalSummaryStartRow = currentRow;
    transactions.forEach((txn, idx) => {
        const chargeAmount = Number(txn.additional_charge_amount || 0);
        const paidAmount = Number(txn.additional_charge_paid_amount || 0);
        const workDesc = txn.additional_charge_work_desc || '';
        const stageDesc = txn.additional_charge_stage_desc || '';
        const balance = chargeAmount - paidAmount;
        addDataRow(worksheet, [workDesc, stageDesc, chargeAmount, paidAmount, { formula: `C${currentRow}-D${currentRow}`, result: balance }], currentRow, [3, 4, 5]);
        currentRow++;
    });
    const additionalSummaryEndRow = currentRow - 1;
    const additionalTotalRow = worksheet.getRow(currentRow);
    additionalTotalRow.getCell(1).value = 'Total';
    additionalTotalRow.getCell(1).font = { bold: true };
    additionalTotalRow.getCell(3).value = { formula: `SUM(C${additionalSummaryStartRow}:C${additionalSummaryEndRow})`, result: 0 };
    additionalTotalRow.getCell(4).value = { formula: `SUM(D${additionalSummaryStartRow}:D${additionalSummaryEndRow})`, result: 0 };
    additionalTotalRow.getCell(5).value = { formula: `SUM(E${additionalSummaryStartRow}:E${additionalSummaryEndRow})`, result: 0 };
    additionalTotalRow.getCell(3).numFmt = '₹#,##0.00';
    additionalTotalRow.getCell(4).numFmt = '₹#,##0.00';
    additionalTotalRow.getCell(5).numFmt = '₹#,##0.00';
    additionalTotalRow.getCell(3).font = { bold: true };
    additionalTotalRow.getCell(4).font = { bold: true };
    additionalTotalRow.getCell(5).font = { bold: true };
    for (let i = 1; i <= 5; i++) {
        additionalTotalRow.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }
    currentRow++;
    return currentRow;
}
export function generateReceiptExcel(data, projectName, sheetName = 'Receipts') {
    return new Promise((resolve) => {
        const workbook = new ExcelJS.Workbook();
        const groups = groupByAgreement(data);
        // ============ SINGLE SHEET: All Data ============
        const sheet = workbook.addWorksheet(sheetName);
        let currentRow = 1;
        groups.forEach((group, groupIndex) => {
            if (groupIndex > 0) {
                currentRow += 2;
            }
            // Customer / Agreement Details
            currentRow = writeCustomerDetails(sheet, group, currentRow);
            // Payment Transactions
            const paymentResult = writePaymentTransactions(sheet, group.paymentTransactions, currentRow);
            currentRow = paymentResult.endRow;
            // Additional Work (if any)
            let additionalResult = { endRow: currentRow, totalRow: null, totalAmount: 0 };
            if (group.additionalWork.length > 0) {
                additionalResult = writeAdditionalWork(sheet, group.additionalWork, currentRow);
                currentRow = additionalResult.endRow;
            }
            // Grand Total
            const paymentTotalRef = paymentResult.totalRow ? `D${paymentResult.totalRow}` : 0;
            const additionalTotalRef = additionalResult.totalRow ? `E${additionalResult.totalRow}` : 0;
            currentRow = writeGrandTotal(sheet, paymentTotalRef, additionalTotalRef, currentRow, group.paymentTransactions.length > 0, group.additionalWork.length > 0);
            // Payment Stage Summary
            if (group.paymentTransactions.length > 0) {
                currentRow = writePaymentStageSummary(sheet, group.paymentTransactions, currentRow);
            }
            // Additional Work Stage Summary
            if (group.additionalWork.length > 0) {
                currentRow++;
                currentRow = writeAdditionalWorkStageSummary(sheet, group.additionalWork, currentRow);
            }
        });
        // Set column widths - need to accommodate both payment and additional work columns
        // Column 1: S.No (6) / Payment Stage (35) / Work Description (35) -> use 35
        // Column 3: Payment Stage (35) / Work Description (35) / Charge Amount (16) -> use 35
        // Column 4: Stage Description (40) / Balance (16) -> use 40
        sheet.getColumn(1).width = 35; // S.No / Payment Stage / Work Description
        sheet.getColumn(2).width = 15; // Payment Date / Date / Schedule Amount / Stage Description
        sheet.getColumn(3).width = 35; // Payment Stage / Additional Work Description / Paid Amount / Charge Amount
        sheet.getColumn(4).width = 40; // Stage Description / Balance
        sheet.getColumn(5).width = 18; // Amount / Charge Amount / Balance
        sheet.getColumn(6).width = 18; // Payment Mode / Paid Amount
        sheet.getColumn(7).width = 18; // Source of Fund / Balance
        sheet.getColumn(8).width = 20; // Remarks / Payment Mode (additional)
        sheet.getColumn(9).width = 30; // Remarks (additional work)
        // Auto filter on first Payment Transactions table
        if (groups.length > 0 && groups[0].paymentTransactions.length > 0) {
            const filterEndRow = groups[0].paymentTransactions.length + 5;
            sheet.autoFilter = { from: { row: 5, column: 1 }, to: { row: filterEndRow, column: 7 } };
        }
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            resolve(blob);
        });
    });
}
export function downloadExcelBlob(blob, fileName) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
